/**
 * Bank-feed advanced-filter document — a versioned value the web filter panel
 * (`BankFeedFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * accounting-import index. One schema + ONE evaluator, both in
 * `@releve/shared` (the places/Impact/AR/AP template), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyBankFeedFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build; same
 * posture here).
 *
 * ── v1: TWO DATE RANGES + THE STATUS FAMILY ────────────────────────────────
 * The bank-feed grid this replaces exposed ONE range filter — the
 * booking-date window; the processed-on window is a deliberate, approved
 * addition. Status was a toolbar SEGMENTED CONTROL applied upstream of the
 * document; it has since MOVED INTO the document as the `feed.feed_status`
 * checkbox family (the string arm this file's earlier revision anticipated,
 * with its own accessor switch beside `dateValue` below). The bank account
 * is still a ROUTE (the workspace is per-account) and the amount/description
 * columns were never filterable.
 *
 * ── THE ONE SEEDED DEFAULT ─────────────────────────────────────────────────
 * Alone among the sibling documents, this one ships a NON-EMPTY default:
 * `DEFAULT_BANK_FEED_FILTER_DOCUMENT` carries `feed_status in ['unmatched']`,
 * so a fresh grid opens on "For review" — the pile a bookkeeper actually
 * works. Two accepted consequences, both deliberate and not special-cased:
 * the filter badge reads "1" on first load, and Clear resets to
 * `EMPTY_BANK_FEED_FILTER_DOCUMENT` (every row), NOT back to the default —
 * the sibling convention, so "Clear" always means "no filters".
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The bank-feed grid ends with ZERO filterable columns.
 *
 * v1 fields are `feed.<field>` over the MAPPED grid rows, not the wire row —
 * evaluation runs AFTER the mapper and BEFORE the grid shell
 * (`hasUpstreamFilters`). The field string carries its namespace, so a later
 * vocabulary (allocation-level fields, say) stays additive.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the two ex-range-filter columns + the status family
// ---------------------------------------------------------------------------
/**
 * The ex-segmented-control family. Its values are the mapped row's THREE
 * buckets ('unmatched' | 'excluded' | 'processed'), never the raw DB status —
 * evaluation runs over `BankFeedGridRow`, which the mapper has already
 * reduced. The schema keeps `values` a plain string list (the shared string
 * primitive's shape); the bucket union is enforced where it is produced.
 */
export const BANK_FEED_FILTER_STRING_FIELDS = ["feed.feed_status"];
export const BANK_FEED_FILTER_DATE_FIELDS = [
    "feed.booking_date",
    "feed.processed_at",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * Two families, so a union — the AR/AP shape. The arms have DISJOINT op sets
 * ('in'/'is_empty' vs the three date ops), which is what lets
 * `matchesCondition` narrow on `op` alone.
 *
 * EMPTY-SET SEMANTICS (from the shared primitive, not re-decided here): an
 * `in` condition with zero values is REJECTED by the schema
 * (`makeStringConditionSchema`'s `min(1)` + its refine). "Nothing checked" is
 * therefore spelled as NO CONDITION AT ALL — the panel drops the family — and
 * never as a condition that would match nothing. `is_empty` remains legal but
 * is unreachable for this family: every mapped row carries one of the three
 * buckets, so no row's status is ever `''`.
 */
const stringConditionSchema = makeStringConditionSchema(BANK_FEED_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(BANK_FEED_FILTER_DATE_FIELDS);
export const bankFeedFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const bankFeedFilterDocumentSchema = makeFilterDocumentSchema(bankFeedFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_BANK_FEED_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/**
 * What a FRESH grid starts on: the "For review" pile. The one seeded default
 * across the sibling filter documents — see the docblock above for the two
 * accepted consequences (badge reads 1 on load; Clear goes to EMPTY, not
 * back to here).
 */
export const DEFAULT_BANK_FEED_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [{ field: "feed.feed_status", op: "in", values: ["unmatched"] }],
};
/*
 * One accessor per field FAMILY, an exhaustive switch over its vocabulary
 * (the reason `sustainabilityFilters` keeps them: a new vocabulary entry is a
 * `TS2366: Function lacks ending return statement` until it is handled here,
 * instead of compiling clean and filtering the wrong column).
 *
 * Compared as STRINGS (lexicographic order is date order for `yyyy-MM-dd`);
 * no `new Date()` round-trip on a date-only value.
 */
function statusValue(row, field) {
    switch (field) {
        case "feed.feed_status":
            return row.feed_status;
    }
}
function dateValue(row, field) {
    switch (field) {
        case "feed.booking_date":
            return row.booking_date;
        case "feed.processed_at":
            return toLocalCalendarDay(row.processed_at);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, statusValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped bank-feed rows (AND of all conditions —
 * v1 `match` is 'all' only). An empty document returns the input array
 * unchanged (identity — callers can rely on reference equality to skip
 * re-renders).
 */
export function applyBankFeedFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveBankFeedConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=bankFeedFilters.js.map